import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRightLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  ALLOWED_OFF_WEEKDAYS,
  SHIFTS,
  SHIFT_SHORT,
  addDays,
  fmtDayMonth,
  mondayOf,
  type Shift,
  type ShiftTimes,
} from '@siva/shared';
import { api, ApiError, post, put } from '../../lib/api.js';
import { errorMessage } from '../../lib/passkey.js';
import { Alert, fmtDate, Spinner } from '../../lib/ui.js';
import { Modal, useConfirm } from '../../lib/Modal.js';
import { DateField } from '../../lib/DateField.js';
import { RosterGrid, type DayRecord, type RosterWorker, type WeekRecords } from '../RosterGrid.js';
import { t } from '../../i18n/index.js';

type WorkerChip = {
  id: string;
  name: string;
  employment_type: 'full_time' | 'part_time' | null;
  shift: Shift;
  source?: string;
};
type Counts = { total: number; full_time: number; part_time: number };
type Day = {
  weekday: number;
  date: string;
  workers: WorkerChip[];
  /** The whole day. The caps apply per shift, so this is a reading, not the rule. */
  counts: Counts;
  by_shift: Record<Shift, Counts>;
};
type Week = {
  week_start: string;
  plan: {
    id: string;
    status: 'draft' | 'published';
    seed: number;
    generated_at: string;
    published_at: string | null;
    infeasibility: { message: string } | null;
  } | null;
  caps: { total: number; full_time: number; part_time: number };
  /** The four windows, for the roster cells. */
  shift_times: ShiftTimes;
  days: Day[];
  unassigned: WorkerChip[];
  /** Workers somebody has decided work all seven days this week. */
  no_off_day: WorkerChip[];
  roster_size: number;
  pending_requests: number;
};
/** The cell that was pressed, and what the day is now. */
type DayChoice = {
  week: Week;
  worker: WorkerChip;
  weekday: number;
  date: string;
  /** From attendance once the day has come, from the plan before. */
  current: Choice | null;
};
type Choice = 'weekly_off' | 'present' | 'absent';
type Suggestion = { swap_with: { id: string; name: string }; their_new_day: number } | null;

const DAY_NAMES: Record<number, string> = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun',
};

/**
 * What to call the week on screen.
 *
 * The three near ones keep their names, because that is how the office talks about them. Any
 * other week is named by its dates, which is the only thing that distinguishes one September
 * week from the next.
 */
const weekLabel = (weekStart: string, thisWeek: string) =>
  weekStart === thisWeek
    ? t('wo_this_week')
    : weekStart === addDays(thisWeek, 7)
      ? t('wo_next_week')
      : weekStart === addDays(thisWeek, 14)
        ? t('wo_week_after_next')
        : `${fmtDayMonth(weekStart)} – ${fmtDate(addDays(weekStart, 6), false)}`;

/** A request the admin has to decide on. */
type PendingSwap = {
  id: string;
  worker: { id: string; name: string };
  counterparty: { id: string; name: string } | null;
  from_date: string;
  to_date: string;
  reason: string | null;
  state: 'waiting_colleague' | 'pending_admin' | 'agreed_pending_admin';
  requested_at: string;
};

export function WeeklyOffPage() {
  const confirm = useConfirm();
  /** The three the queue watches: this week, next, and the one after. */
  const [weeks, setWeeks] = useState<Week[] | null>(null);
  const [thisWeek, setThisWeek] = useState('');
  /**
   * The Monday on screen, and every week we have fetched so far.
   *
   * It used to be an index into those three, which is why there was no way back: the office keeps
   * this sheet to answer "when was Ravi off in September", and three buttons could only ever
   * answer the question about next week.
   */
  const [shown, setShown] = useState('');
  const [views, setViews] = useState<Record<string, Week>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'error' | 'success' | 'info'; text: string } | null>(null);
  const [blocked, setBlocked] = useState<{
    week: string;
    user: WorkerChip;
    weekday: number;
    violations: string[];
    suggestion: Suggestion;
  } | null>(null);

  /** What each day that has come actually was, per week. */
  const [records, setRecords] = useState<Record<string, WeekRecords>>({});
  const [today, setToday] = useState('');
  const [choice, setChoice] = useState<DayChoice | null>(null);
  /** The work date now. Before any started week is loaded, every day shown is still to come. */
  const now = today || (thisWeek ? addDays(thisWeek, -1) : '');
  const [swapsByWeek, setSwapsByWeek] = useState<Record<string, PendingSwap[]>>({});
  /**
   * A notification about a swap links here with the week the swap belongs to, so it opens on
   * that week rather than on this one — which is where the admin landed, found no request, and
   * asked whether they were meant to move the day by hand.
   */
  const [params] = useSearchParams();
  const wantedWeek = params.get('week');

  const load = useCallback(async () => {
    try {
      // The three near weeks come every time: the queue is drawn from them, and a request for
      // the week after next has to be visible whichever week is being looked at.
      const r = await api<{ weeks: Week[]; this_week: string }>('/admin/weekly-off');
      setWeeks(r.weeks);
      setThisWeek(r.this_week);
      const near = Object.fromEntries(r.weeks.map((w) => [w.week_start, w]));
      const want = shown || wantedWeek || r.this_week;
      // A week outside those three — any week in the past, or further ahead — is fetched on its
      // own. The API has taken ?week= all along; nothing on the page ever asked for one.
      const far = near[want]
        ? null
        : ((await api<{ weeks: Week[] }>(`/admin/weekly-off?week=${want}`)).weeks[0] ?? null);
      setViews((v) => ({ ...v, ...near, ...(far ? { [want]: far } : {}) }));
      // Only if nobody has picked a week since this load began. The first load could land after
      // a click on Previous and put the page back on this week — the click seemed to do nothing
      // (seen on the production stack, 15 October 2026).
      setShown((cur) => cur || want);
      // Requests waiting on an admin, per week: the three, and the one being looked at.
      const forWeeks = [...new Set([...r.weeks.map((w) => w.week_start), want])];
      const entries = await Promise.all(
        forWeeks.map(async (ws) => {
          try {
            const s = await api<{ swaps: PendingSwap[] }>(`/admin/weekly-off/${ws}/swaps`);
            return [ws, s.swaps] as const;
          } catch {
            return [ws, []] as const;
          }
        }),
      );
      setSwapsByWeek(Object.fromEntries(entries));
      // Present and absent for every day that has come (the owner, 21 October 2026). The same
      // week view Attendance uses, corrections applied. A week still to come is not asked for.
      if (want <= r.this_week) {
        const att = await api<{
          today: string;
          workers: {
            id: string;
            days: Record<string, { status: DayRecord['status']; adjustment: unknown } | null>;
          }[];
        }>(`/admin/attendance/week?date=${want}`);
        setToday(att.today);
        setRecords((m) => ({
          ...m,
          [want]: Object.fromEntries(
            att.workers.map((w) => [
              w.id,
              Object.fromEntries(
                Object.entries(w.days).map(([d, c]) => [
                  d,
                  c ? { status: c.status, adjusted: c.adjustment != null } : null,
                ]),
              ),
            ]),
          ),
        }));
      }
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    }
  }, [wantedWeek, shown]);
  useEffect(() => {
    void load();
  }, [load]);

  /**
   * A link that names a week wins, even from a page already open.
   *
   * A notification about a swap carries ?week=, and an admin who is already on this page looking
   * at another week would otherwise press Review and see nothing change.
   */
  useEffect(() => {
    if (wantedWeek) setShown(wantedWeek);
  }, [wantedWeek]);

  const call = async (fn: () => Promise<unknown>, okText?: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      await load();
      if (okText) setMsg({ kind: 'success', text: okText });
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  /** Approve applies the swap; reject closes it. Both may carry a note for the workers. */
  const decideSwap = async (id: string, approve: boolean) => {
    const note = approve
      ? undefined
      : (await confirm({
            title: t('wo_swap_reject'),
            body: t('wo_swap_reject_body'),
            confirmLabel: t('wo_swap_reject'),
            danger: true,
          }))
        ? undefined
        : null;
    if (note === null) return;
    await call(
      () => post(`/admin/weekly-off/swaps/${id}/${approve ? 'approve' : 'reject'}`, {}),
      approve ? t('wo_swap_approved') : t('wo_swap_rejected'),
    );
  };

  /** Show another week. The fetch happens in load(), which depends on what is shown. */
  const go = (weekStart: string) => {
    setShown(weekStart);
    setBlocked(null);
    setMsg(null);
  };

  const move = async (weekStart: string, user: WorkerChip, weekday: number, override = false) => {
    setBusy(true);
    setMsg(null);
    setBlocked(null);
    try {
      await post(`/admin/weekly-off/${weekStart}/move`, {
        user_id: user.id,
        weekday,
        ...(override ? { override: true } : {}),
      });
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'cap_violation') {
        setBlocked({
          week: weekStart,
          user,
          weekday,
          violations: (err.details.violations as string[]) ?? [],
          suggestion: (err.details.suggestion as Suggestion) ?? null,
        });
      } else {
        setMsg({ kind: 'error', text: errorMessage(err) });
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * Do it anyway.
   *
   * The caps stop the floor emptying by accident; they are not meant to outrank the person who
   * can see the floor and knows the day is covered. The violations they just read are recorded
   * in the audit trail with the move, so "why was Thursday short" has an answer later.
   */
  /** They work all seven days this week, or they are given a day back. */
  const setNoOff = async (weekStart: string, user: WorkerChip, none: boolean) => {
    setBusy(true);
    setMsg(null);
    setBlocked(null);
    try {
      await post(`/admin/weekly-off/${weekStart}/no-off`, { user_id: user.id, none });
      await load();
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  /**
   * Ask what a day should be.
   *
   * The cell is the control, and it is also the thing you press while reading along a row —
   * "right now when I click on any time here, it is changing to weekly off" — so it has always
   * asked first. It now asks which: Weekly off, Present or Absent (the owner, 21 October 2026),
   * so a day can be put right on the sheet where it was noticed.
   */
  const openDay = (
    wk: Week,
    user: RosterWorker,
    weekday: number,
    date: string,
    rec: DayRecord | null,
  ) => {
    const planned = wk.days
      .find((d) => d.weekday === weekday)
      ?.workers.some((w) => w.id === user.id);
    // Today, somebody not in yet is not absent yet; until the day closes it is what the plan says.
    const notYet = rec?.status === 'absent' && date === now && !rec.adjusted;
    const current: Choice | null =
      !rec || notYet
        ? planned
          ? 'weekly_off'
          : null
        : rec.status === 'weekly_off'
          ? 'weekly_off'
          : rec.status === 'absent'
            ? 'absent'
            : rec.status === 'holiday'
              ? null
              : 'present';
    setMsg(null);
    setBlocked(null);
    setChoice({ week: wk, worker: user as WorkerChip, weekday, date, current });
  };

  /**
   * Make it so.
   *
   * Weekly off on a day that has not gone moves the off day in the plan, through the caps, as the
   * cell always did. Anything about a day that has gone — Present, Absent, or Weekly off after the
   * fact — and Present or Absent today, is a day correction: the plan is left as it was, and the
   * correction carries its reason, exactly as one made from Attendance.
   */
  const saveDay = async (c: DayChoice, pick: Choice, note: string) => {
    if (pick === 'weekly_off' && c.date >= now) {
      setChoice(null);
      await move(c.week.week_start, c.worker, c.weekday);
      return;
    }
    await put(`/admin/workers/${c.worker.id}/day/adjustment`, { date: c.date, status: pick, note });
    setChoice(null);
    await load();
    setMsg({
      kind: 'success',
      text: t('wo_day_saved', { name: c.worker.name, date: fmtDate(c.date, false) }),
    });
  };

  const moveAnyway = () => {
    if (!blocked) return;
    const b = blocked;
    void move(b.week, b.user, b.weekday, true);
  };

  const applySwap = () => {
    if (!blocked?.suggestion) return;
    const b = blocked;
    void call(async () => {
      await post(`/admin/weekly-off/${b.week}/swap`, {
        user_id: b.user.id,
        weekday: b.weekday,
        other_user_id: b.suggestion!.swap_with.id,
        other_weekday: b.suggestion!.their_new_day,
      });
      setBlocked(null);
    }, t('wo_swap_done'));
  };

  const generate = async (week: Week) => {
    if (
      week.plan &&
      !(await confirm({
        title: t('wo_regenerate'),
        body: t('wo_regenerate_confirm'),
        confirmLabel: t('wo_regenerate'),
        danger: true,
      }))
    )
      return;
    void call(() => post(`/admin/weekly-off/${week.week_start}/generate`), t('wo_generated'));
  };

  const publish = async (week: Week) => {
    if (
      !(await confirm({
        title: t('wo_publish'),
        body: t('wo_publish_confirm', { week: fmtDate(week.week_start, false) }),
        confirmLabel: t('wo_publish'),
      }))
    )
      return;
    void call(() => post(`/admin/weekly-off/${week.week_start}/publish`), t('wo_published'));
  };

  const week = views[shown];
  if (!weeks || !week) return <Spinner />;
  /**
   * A week that has already finished is a record, not a plan.
   *
   * Nothing on it can be pressed. Changing an off day in a week that has been would change what
   * the attendance for those days means — a day counted as a week off rather than an absence —
   * and that belongs to the day-adjustment screen, which asks for a reason and keeps it.
   */
  const past = shown < thisWeek;

  /** One request, with what it asks and the two answers. */
  const swapRow = (sw: PendingSwap, weekStart?: string) => (
    <li key={sw.id} className="swap-row">
      <div>
        <strong>{sw.worker.name}</strong>{' '}
        <span className="muted">
          {fmtDayMonth(sw.from_date)} → {fmtDayMonth(sw.to_date)}
        </span>
        {weekStart && (
          <span className="badge neutral wo-week-tag">
            {t('wo_for_week', { date: fmtDate(weekStart, false) })}
          </span>
        )}
        <div className="muted small-text">
          {sw.counterparty
            ? sw.state === 'agreed_pending_admin'
              ? t('wo_swap_agreed', { name: sw.counterparty.name })
              : t('wo_swap_waiting', { name: sw.counterparty.name })
            : t('wo_swap_no_colleague')}
          {' · '}
          {fmtDate(sw.requested_at, true)}
        </div>
        {sw.reason && <div className="muted small-text">“{sw.reason}”</div>}
      </div>
      <span className="row">
        <button
          type="button"
          className="small"
          disabled={busy || sw.state === 'waiting_colleague'}
          title={sw.state === 'waiting_colleague' ? t('wo_swap_wait_first') : undefined}
          onClick={() => void decideSwap(sw.id, true)}
        >
          {t('wo_swap_approve')}
        </button>
        <button
          type="button"
          className="small secondary"
          disabled={busy}
          onClick={() => void decideSwap(sw.id, false)}
        >
          {t('wo_swap_reject')}
        </button>
      </span>
    </li>
  );
  /** Everything waiting on an admin, whichever week it is for. */
  const queue = weeks.flatMap((w) =>
    (swapsByWeek[w.week_start] ?? []).map((sw) => [w, sw] as const),
  );

  /**
   * Which shifts this week actually has people on.
   *
   * With everybody on the first shift — which is where the whole roster starts — the day column
   * reads exactly as it did before shifts existed: one cap line, no prefix. The second line and
   * the chip markers appear when there is a second shift to tell apart, and not before.
   */
  const shiftsInUse = SHIFTS.filter(
    (s) =>
      week.unassigned.some((w) => w.shift === s) ||
      week.days.some((d) => d.workers.some((w) => w.shift === s)),
  );
  const shownShifts = shiftsInUse.length > 1 ? shiftsInUse : [];

  return (
    <div className="stack">
      {/* The queue first: a request for the week after next was invisible on this week's tab,
          and "Review" from a notification landed here with nothing to review. */}
      {queue.length > 0 && (
        <section className="card swap-queue" id="swap-queue">
          <h3>
            {t('wo_swap_queue')} <span className="badge warn">{queue.length}</span>
          </h3>
          <p className="muted">{t('wo_swap_queue_sub')}</p>
          <ul className="list">{queue.map(([w, sw]) => swapRow(sw, w.week_start))}</ul>
        </section>
      )}
      <section className="card">
        <div className="row between">
          <h2>
            {t('nav_weekly_off')} <span className="wo-which">{weekLabel(shown, thisWeek)}</span>
          </h2>
          <span className="row">
            <span className="wo-legend" aria-hidden="true">
              <span className="chip-dot ft" /> {t('full_time')}
              <span className="chip-dot pt" /> {t('part_time')}
            </span>
            {/* Any week, forwards or back. The date picker takes any day and lands on that
                day's Monday, because nobody thinks in Mondays — they think "the week Deepavali
                fell in". */}
            <span className="wo-weeks">
              <button
                type="button"
                className="small secondary wo-step"
                onClick={() => go(addDays(shown, -7))}
                aria-label={t('wo_prev_week')}
                title={t('wo_prev_week')}
              >
                <ChevronLeft size={16} aria-hidden="true" />
              </button>
              {/* The project's own field rather than a bare date input: it reads DD/MM/YYYY
                  whatever the browser's locale says, which is what every other date on the
                  admin does and what the office writes on paper. */}
              <DateField
                className="wo-pick"
                label={t('wo_pick_week')}
                value={shown}
                onChange={(ymd) => go(mondayOf(ymd))}
              />
              <button
                type="button"
                className="small secondary wo-step"
                onClick={() => go(addDays(shown, 7))}
                aria-label={t('wo_next_week_btn')}
                title={t('wo_next_week_btn')}
              >
                <ChevronRight size={16} aria-hidden="true" />
              </button>
              {shown !== thisWeek && (
                <button type="button" className="small secondary" onClick={() => go(thisWeek)}>
                  {t('wo_this_week')}
                </button>
              )}
            </span>
          </span>
        </div>
        {msg && <Alert kind={msg.kind}>{msg.text}</Alert>}
        {past && <p className="muted wo-past-note">{t('wo_past_note')}</p>}

        <p className="row">
          <span className={`badge status-${week.plan?.status ?? 'none'}`}>
            {week.plan
              ? week.plan.status === 'published'
                ? t('wo_status_published')
                : t('wo_status_draft')
              : t('wo_status_none')}
          </span>
          <span className="muted">
            {t('wo_roster', { n: week.roster_size })}
            {week.pending_requests ? ` · ${t('wo_pending', { n: week.pending_requests })}` : ''}
          </span>
          <span className="row" style={{ marginLeft: 'auto' }}>
            {!past && (!week.plan || week.plan.status === 'draft') && (
              <button
                type="button"
                className="secondary"
                onClick={() => generate(week)}
                disabled={busy}
              >
                {week.plan ? t('wo_regenerate') : t('wo_generate')}
              </button>
            )}
            {!past && week.plan?.status === 'draft' && week.unassigned.length === 0 && (
              <button type="button" onClick={() => publish(week)} disabled={busy}>
                {t('wo_publish')}
              </button>
            )}
          </span>
        </p>
        {week.plan?.infeasibility && (
          <Alert kind="warn">
            {week.plan.infeasibility.message}
            {week.unassigned.length > 0 &&
              ` ${t('wo_partial_hint', { n: week.unassigned.length })}`}
          </Alert>
        )}

        {blocked && (
          <div className="confirm-panel">
            <strong>
              {t('wo_blocked', { name: blocked.user.name, day: DAY_NAMES[blocked.weekday] ?? '' })}
            </strong>
            <p className="muted">{blocked.violations.join('; ')}</p>
            {blocked.suggestion ? (
              <p className="row">
                <span>
                  {t('wo_suggest', {
                    other: blocked.suggestion.swap_with.name,
                    day: DAY_NAMES[blocked.suggestion.their_new_day] ?? '',
                  })}
                </span>
                <button type="button" onClick={applySwap} disabled={busy}>
                  {t('wo_apply_swap')}
                </button>
                <button type="button" className="secondary" onClick={moveAnyway} disabled={busy}>
                  {t('wo_move_anyway')}
                </button>
                <button type="button" className="secondary" onClick={() => setBlocked(null)}>
                  {t('cancel')}
                </button>
              </p>
            ) : (
              <p className="muted">{t('wo_no_swap')}</p>
            )}
          </div>
        )}

        {(swapsByWeek[week.week_start] ?? []).length > 0 && (
          <div className="wo-swaps">
            <h4>
              {t('wo_pending_swaps')}
              <span className="badge warn">{(swapsByWeek[week.week_start] ?? []).length}</span>
            </h4>
            <ul className="list">
              {(swapsByWeek[week.week_start] ?? []).map((sw) => swapRow(sw))}
            </ul>
          </div>
        )}

        <div className={`wo-unassigned${week.unassigned.length ? ' has-people' : ''}`}>
          <h4>
            {t('wo_unassigned')}
            {week.unassigned.length > 0 && (
              <span className="badge bad">{week.unassigned.length}</span>
            )}
          </h4>
          <ul className="list chips">
            {week.unassigned.length === 0 && <li className="muted">{t('wo_none')}</li>}
            {week.unassigned.map((w) => (
              <Chip
                key={w.id}
                w={w}
                current={null}
                onMove={(wd) => void move(week.week_start, w, wd)}
                disabled={busy || past}
                showShift={shownShifts.length > 0}
              />
            ))}
          </ul>
        </div>

        {/*
          The week as the sheet the office kept by hand: a row per person, a column per day, and
          the shift window in every cell but the one that says Week off. The board of seven day
          columns answered "who is off on Thursday" — the question a cap asks — and this answers
          "when is Ravi in this week", which is the one the floor asks.
        */}
        <RosterGrid
          week={week}
          busy={busy}
          readOnly={past}
          records={records[week.week_start] ?? {}}
          today={now}
          onCell={(w, weekday, date, rec) => openDay(week, w, weekday, date, rec)}
          onNoOff={(w, none) => void setNoOff(week.week_start, w, none)}
        />
      </section>
      <DayChoiceModal
        choice={choice}
        today={now}
        onClose={() => setChoice(null)}
        onSave={saveDay}
      />
    </div>
  );
}

/**
 * Weekly off, Present or Absent, for one person on one day.
 *
 * A day still to come can only be an off day — nobody is present in advance — so the other two
 * are shown and not offered. A change that is a day correction needs a reason, the same rule as
 * every other correction; moving an off day that has not gone does not, as before.
 */
function DayChoiceModal({
  choice,
  today,
  onClose,
  onSave,
}: {
  choice: DayChoice | null;
  today: string;
  onClose: () => void;
  onSave: (c: DayChoice, pick: Choice, note: string) => Promise<void>;
}) {
  const [pick, setPick] = useState<Choice | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    // A day still to come has one possible answer, so it opens with it chosen: one press, as the
    // old "Set Week Off" was.
    setPick(choice ? (choice.date > today ? 'weekly_off' : choice.current) : null);
    setNote('');
    setError(null);
  }, [choice, today]);
  if (!choice) return null;
  const future = choice.date > today;
  const needsNote = !(pick === 'weekly_off' && choice.date >= today);
  const changed = pick !== null && pick !== choice.current;
  const canSave = changed && (!needsNote || note.trim().length > 0) && !busy;
  const save = async () => {
    if (!pick || !canSave) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(choice, pick, note.trim());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const options: { value: Choice; label: string }[] = [
    { value: 'weekly_off', label: t('wo_choice_off') },
    { value: 'present', label: t('wo_choice_present') },
    { value: 'absent', label: t('wo_choice_absent') },
  ];
  return (
    <Modal
      open
      title={t('wo_day_title', { name: choice.worker.name })}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="secondary" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" onClick={() => void save()} disabled={!canSave}>
            {pick === 'present'
              ? t('wo_mark_present')
              : pick === 'absent'
                ? t('wo_mark_absent')
                : t('wo_set_off_do')}
          </button>
        </>
      }
    >
      <p className="muted wo-day-date">{fmtDate(choice.date, false)}</p>
      <div
        className="wo-day-options"
        role="radiogroup"
        aria-label={t('wo_day_title', { name: choice.worker.name })}
      >
        {options.map((o) => {
          const off = future && o.value !== 'weekly_off';
          return (
            <label key={o.value} className={`wo-day-option${off ? ' is-disabled' : ''}`}>
              <input
                type="radio"
                name="wo-day"
                value={o.value}
                checked={pick === o.value}
                disabled={off}
                onChange={() => setPick(o.value)}
              />
              <span>{o.label}</span>
              {choice.current === o.value && (
                <span className="badge neutral">{t('wo_choice_now')}</span>
              )}
            </label>
          );
        })}
      </div>
      {future && <p className="muted small-text">{t('wo_choice_future')}</p>}
      {needsNote && (
        <label className="wo-day-note">
          {t('wo_choice_note')}
          <textarea
            rows={2}
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('wo_choice_note_ph')}
          />
        </label>
      )}
      {error && <Alert kind="error">{error}</Alert>}
    </Modal>
  );
}

function Chip({
  w,
  current,
  onMove,
  disabled,
  showShift = false,
}: {
  w: WorkerChip;
  current: number | null;
  onMove: (weekday: number) => void;
  disabled: boolean;
  /** Only when the week has both shifts: otherwise every chip would carry the same marker. */
  showShift?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const type = w.employment_type === 'full_time' ? 'FT' : 'PT';
  return (
    <li
      className={`chip ${w.employment_type === 'full_time' ? 'ft' : 'pt'}${open ? ' open' : ''}`}
      draggable
      onDragStart={(e) => e.dataTransfer.setData('text/worker', w.id)}
      title={w.source === 'admin' ? t('wo_moved_by_admin') : ''}
    >
      <span className="chip-name">
        {w.name} <small>{type}</small>
        {showShift && <small className="chip-shift">{SHIFT_SHORT[w.shift]}</small>}
      </span>
      <button
        type="button"
        className="chip-move"
        aria-label={t('wo_move_to', { name: w.name })}
        title={t('wo_move_hint')}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
      >
        <ArrowRightLeft size={14} aria-hidden="true" />
        <span className="chip-move-label">{t('wo_move')}</span>
      </button>
      {open && (
        <span className="chip-days" role="group" aria-label={t('wo_move_to', { name: w.name })}>
          {ALLOWED_OFF_WEEKDAYS.filter((d) => d !== current).map((d) => (
            <button
              key={d}
              type="button"
              className="small secondary"
              disabled={disabled}
              onClick={() => {
                setOpen(false);
                onMove(d);
              }}
            >
              {DAY_NAMES[d]}
            </button>
          ))}
        </span>
      )}
    </li>
  );
}
